import { MultiSelectDropdown } from 'ck-components';
import { useForm } from 'react-hook-form';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import toast from 'react-hot-toast';

import { useEditOrganizerCategories } from '../../queries/organizer/useEditOrganizerCategories';
import { useOrganizer } from '../../queries/organizer/useOrganizers';

interface Props {
  committeeId: string;
}

interface CategoriesFormValues {
  categories: string[];
}

export interface CategoriesFormHandle {
  /** Validates and saves the selected categories. Resolves false on failure so the
   *  caller can decide whether to proceed with anything that depends on it. */
  submit: () => Promise<boolean>;
}

export const EditOrganizerCategoriesForm = forwardRef<
  CategoriesFormHandle,
  Props
>(({ committeeId }, ref) => {
  // categoryOptions is the full catalog (not just this committee's selection), each entry
  // flagged with `enabled`. That single field replaces what used to be two separate fetches -
  // the committee-independent options list and this committee's selected subset - so there's
  // no longer a window where one has loaded and the other hasn't.
  const Q_organizer = useOrganizer(committeeId);
  // Sorted defensively rather than trusting array order to stay stable across releases/refetches
  // - the backend happens to return it pre-sorted today, but that's an implementation detail.
  const categoryOptions = Q_organizer.data?.categoryOptions
    ? [...Q_organizer.data.categoryOptions].sort(
        (a, b) => a.sortOrder - b.sortOrder
      )
    : undefined;

  const form = useForm<CategoriesFormValues>({
    defaultValues: { categories: [] },
  });
  const M_editCategories = useEditOrganizerCategories(committeeId);

  // Only prefill once per committee, so a background refetch doesn't clobber unsaved edits.
  // Safe because the parent remounts this component (via `key`) on every organizer switch,
  // so this ref never has to survive across committees - only across refetches of the same one.
  const prefilledCommitteeId = useRef<string | undefined>();

  useEffect(() => {
    if (categoryOptions && prefilledCommitteeId.current !== committeeId) {
      prefilledCommitteeId.current = committeeId;
      form.setValue(
        'categories',
        categoryOptions
          .filter((option) => option.enabled)
          .map((option) => option.id)
      );
    }
  }, [categoryOptions, committeeId]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      // categoryOptions is only undefined while Q_organizer is still loading (an empty catalog
      // is a defined but empty array, which is fine to save). Without this guard, a save
      // triggered before that load resolves would read the form's untouched [] default and PUT
      // it, silently clearing every category this committee actually has.
      if (!categoryOptions) {
        toast.error(
          'Sidan har inte laddat klart än - försök igen om en liten stund'
        );
        return false;
      }

      const valid = await form.trigger();
      if (!valid) return false;

      try {
        await M_editCategories.mutateAsync({
          categoryIds: form.getValues('categories'),
        });
        return true;
      } catch {
        // The mutation's own toast.promise already surfaced the error.
        return false;
      }
    },
  }));

  if (!categoryOptions || categoryOptions.length === 0) {
    return null;
  }

  return (
    <MultiSelectDropdown
      name='categories'
      parentForm={form}
      optionsData={categoryOptions.map((option) => option.id)}
      labelFromOption={(id) =>
        categoryOptions.find((option) => option.id === id)?.nameSwedish ?? ''
      }
      label='Kategorier'
      description='Gör det lättare att hitta din organisation i appen'
    />
  );
});

EditOrganizerCategoriesForm.displayName = 'EditOrganizerCategoriesForm';
